import { Gem, Image, Play, Flag, X, RotateCcw } from 'lucide-react';
import { useState, useEffect } from 'react';
import type { Submission } from '@jemmi/schemas';

import { cn } from '@/lib/cn';

type Props = {
  submission: Submission;
  isVoted: boolean;
  onVote?: (submissionId: string) => void;
  onFlag?: (submissionId: string, reason: string) => void;
  onUndoFlag?: (submissionId: string) => void;
};

/**
 * Card that displays a user's submission to a challenge.
 *
 * Shows media (image or video placeholder), caption, creator, timestamp,
 * vote button, and flag button for reporting inappropriate content.
 *
 * Voting behavior:
 *  - isVoted: true if this submission is currently the user's vote
 *  - onVote: called when user clicks vote button
 *
 * Flagging behavior:
 *  - onFlag: called when user reports inappropriate content
 *  - Reasons: sexual, violent, hate_speech, harassment, other
 *  - Flagged submissions become temporarily unavailable
 *  - Moderation queue reviews and either approves or removes
 *  - Users have 10 seconds to undo the flag if clicked by accident
 */
export function SubmissionCard({ submission, isVoted, onVote, onFlag, onUndoFlag }: Props) {
  const [animatingVote, setAnimatingVote] = useState(false);
  const [showFlagMenu, setShowFlagMenu] = useState(false);
  const [flagged, setFlagged] = useState(false);
  const [undoCountdown, setUndoCountdown] = useState(0);
  const [flagReason, setFlagReason] = useState<string | null>(null);

  // Countdown timer for undo
  useEffect(() => {
    if (undoCountdown <= 0) return;

    const timer = setInterval(() => {
      setUndoCountdown((prev) => {
        if (prev <= 1) {
          setFlagged(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [undoCountdown]);

  const handleVote = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnimatingVote(true);
    window.setTimeout(() => setAnimatingVote(false), 700);
    onVote?.(submission.id);
  };

  const handleFlag = (reason: string) => {
    setFlagged(true);
    setShowFlagMenu(false);
    setFlagReason(reason);
    setUndoCountdown(10); // 10 second grace period
    onFlag?.(submission.id, reason);
  };

  const handleUndoFlag = () => {
    setFlagged(false);
    setUndoCountdown(0);
    setFlagReason(null);
    onUndoFlag?.(submission.id);
  };

  const displayedJems = isVoted ? submission.jemCount + 1 : submission.jemCount;
  const createdAgo = getTimeAgo(submission.createdAt);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white transition-all hover:shadow-brand-md">
      {/* Media container */}
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
        <img
          src={submission.mediaUrl}
          alt="Submission"
          className="h-full w-full object-cover"
        />
        {/* Media type indicator */}
        <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm">
          {submission.mediaType === 'video' ? (
            <Play className="h-4 w-4 fill-white" />
          ) : (
            <Image className="h-4 w-4" />
          )}
        </div>

        {/* Flag button (top-left) */}
        <div className="absolute left-3 top-3">
          <div className="relative">
            {!flagged ? (
              <>
                <button
                  type="button"
                  onClick={() => setShowFlagMenu(!showFlagMenu)}
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full transition-all',
                    'bg-black/50 text-white hover:bg-black/70 backdrop-blur-sm',
                  )}
                  title="Report inappropriate content"
                >
                  <Flag className="h-4 w-4" />
                </button>

                {/* Flag reason dropdown */}
                {showFlagMenu && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg z-10 border border-slate-200">
                    <div className="p-2 text-xs font-semibold text-slate-600 uppercase tracking-wide border-b border-slate-100">
                      Report as
                    </div>
                    <button
                      type="button"
                      onClick={() => handleFlag('sexual')}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      Sexual content
                    </button>
                    <button
                      type="button"
                      onClick={() => handleFlag('violent')}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      Violence
                    </button>
                    <button
                      type="button"
                      onClick={() => handleFlag('hate_speech')}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      Hate speech
                    </button>
                    <button
                      type="button"
                      onClick={() => handleFlag('harassment')}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      Harassment
                    </button>
                    <button
                      type="button"
                      onClick={() => handleFlag('other')}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      Other
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Flagged state with undo option */}
                <button
                  type="button"
                  onClick={handleUndoFlag}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-all"
                  title="Undo flag report"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>

                {/* Undo confirmation tooltip */}
                <div className="absolute top-full left-0 mt-1 bg-red-500 text-white text-xs font-semibold px-3 py-2 rounded whitespace-nowrap z-10 flex items-center gap-2">
                  <Flag className="h-3 w-3" fill="currentColor" />
                  Reported
                </div>

                {/* Undo countdown timer */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-12 bg-slate-900 text-white text-xs font-semibold px-2 py-1 rounded whitespace-nowrap z-10">
                  Undo in {undoCountdown}s
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Caption */}
        {submission.caption && (
          <p className="mb-3 text-sm text-slate-700 line-clamp-2">{submission.caption}</p>
        )}

        {/* Creator + timestamp */}
        <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-3">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-300 to-brand-600 text-[10px] font-semibold text-white">
            {submission.user.displayName
              .split(' ')
              .map((p) => p[0])
              .slice(0, 2)
              .join('')}
          </div>
          <div>
            <p className="text-xs font-medium text-slate-900">{submission.user.displayName}</p>
            <p className="text-[11px] text-slate-500">{createdAgo}</p>
          </div>
        </div>

        {/* Vote button */}
        <button
          type="button"
          onClick={handleVote}
          aria-label={isVoted ? 'Remove your vote from this submission' : 'Vote for this submission'}
          className={cn(
            'relative flex w-full items-center justify-center gap-1.5 rounded-full py-2 text-xs font-semibold transition-all',
            isVoted
              ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200'
              : 'bg-gradient-to-br from-brand-500 to-brand-700 text-white hover:shadow-brand-md active:scale-95',
          )}
        >
          <Gem
            className={cn('h-3.5 w-3.5', animatingVote && 'animate-bounce')}
            fill={isVoted ? '#1E6BE6' : 'currentColor'}
          />
          <span className="tabular-nums">{displayedJems}</span>
          {animatingVote && (
            <span className="absolute -right-1 -top-2 animate-ping text-[10px] font-bold text-brand-600">
              +1
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

function getTimeAgo(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

